import * as A from 'fp-ts/Array';
import { flow, identity, pipe } from 'fp-ts/function';
import { not } from 'fp-ts/Predicate';
import * as T from 'fp-ts/Tuple';

import type { Node, RichContent } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';

import { and } from '../fp-utils';
import { extract } from '../RicosContentAPI/extract';
import { modify } from '../RicosContentAPI/modify';
import { fromEntries } from '../utils';
import type { NodeFilter } from '../visitNodes/index';
import { getFilteredNodes } from '../visitNodes/index';
import { DEFAULT_COUNTABLE_TYPES, isNonTextual, isTextContainer as isTextual } from './types';

type NodeWordCount = [Node['id'], number];

type ContentLimits =
  | {
      nodeCount: number;
    }
  | {
      wordCount: number;
    }
  | {
      maxPlugins: number;
    };

/**
 * These options apply only to filtering RichContent.
 */
export type TruncateOptions = {
  /**
   * By default empty paragraphs are merged. Set this to `true` to keep them separate.
   */
  keepEmptyParagraphs: boolean;
  /**
   * Use this function to select the types of nodes that will be included in the blocksCount for truncation.
   */
  selectCountableNodes?: NodeFilter;
};

const isNonTextualNode = (node: Node) => isNonTextual(node.type);
const isTextualNode = (node: Node) => isTextual(node.type);
const isEmptyParagraph = (node: Node) =>
  node.type === Node_Type.PARAGRAPH && node.nodes?.length === 0;
const isTextNode = (node: Node) => node.type === Node_Type.TEXT;

const toId = (node: Node) => node.id;

const toWords = (node: Node) =>
  extract(node)
    .map(({ textData }) => textData?.text ?? '')
    .get()
    .flat()
    .join('')
    .split(' ')
    .filter(w => w !== '');

const toTextLength = (node: Node) =>
  extract(node)
    .map(({ textData }) => textData?.text?.length ?? 0)
    .get()
    .flat()
    .reduce((acc, l) => acc + l, 0);

const toWordCount = flow(toWords, A.size);

const toNodeWordCount = (node: Node): NodeWordCount =>
  pipe([node, node], T.bimap(toWordCount, toId));

const limitToCount =
  (count: number) =>
  (nodeWordCount: NodeWordCount[]): NodeWordCount[] =>
    nodeWordCount.reduce<[NodeWordCount[], number]>(
      ([acc, wordLimit]: [NodeWordCount[], number], [id, nodeCount]: NodeWordCount) => [
        [...acc, [id, Math.min(wordLimit, nodeCount)]],
        Math.max(wordLimit - nodeCount, 0),
      ],
      [[], count]
    )[0];

const limitWords = (node: Node, count: number): Node => {
  const wordCount = toWordCount(node);
  if (wordCount === count) {
    return node;
  }

  const truncatedLength = toWords(node).slice(0, count).join(' ').length;

  const nodeTextLengths = node.nodes?.filter(isTextNode).map(toTextLength) ?? [];

  const reducedLengths = nodeTextLengths.reduce<[number[], number]>(
    ([reducedLengths, limit], nodeLength: number) =>
      [[...reducedLengths, Math.min(nodeLength, limit)], Math.max(limit - nodeLength, 0)] as [
        number[],
        number
      ],
    [[], truncatedLength]
  )[0];

  // Append … if current node text length is reduced but not empty, OR the next node text node reduced to zero.
  const shouldAppendEllipsis = (i: number) =>
    (reducedLengths[i] > 0 && reducedLengths[i] < nodeTextLengths[i]) ||
    (reducedLengths[i] > 0 && reducedLengths[i + 1] === 0);

  const nodes: Node[] =
    node.nodes
      ?.filter(isTextNode)
      .map((n, i) => {
        return {
          ...n,
          textData: {
            ...n.textData,
            decorations: n.textData?.decorations ?? [],
            text:
              n.textData?.text
                ?.slice(0, reducedLengths[i])
                .concat(shouldAppendEllipsis(i) ? '…' : '') ?? '',
          },
        };
      })
      .filter(n => n.textData?.text?.length) ?? [];

  return { ...node, nodes };
};

const defaultSelectCountableNodes: NodeFilter = (node: Node) =>
  !isEmptyParagraph(node) && DEFAULT_COUNTABLE_TYPES.includes(node.type);

export const getCountableNodes = getFilteredNodes;

const applyToContent =
  (content: RichContent) =>
  (nodeWordCount: Record<Node['id'], number>): RichContent =>
    modify(content)
      .filter(n => Object.keys(nodeWordCount).includes(n.id))
      .set(n => (nodeWordCount[n.id] === 0 ? [] : [limitWords(n, nodeWordCount[n.id])]));

const byNodeCount =
  (count: number, selectCountableNodes: NodeFilter) =>
  (content: RichContent): RichContent =>
    pipe(
      content,
      getCountableNodes(selectCountableNodes),
      nodes => nodes.slice(count).map(toId),
      removeNodesById(content)
    );

const byWordsCount =
  (count: number) =>
  (content: RichContent): RichContent =>
    pipe(
      extract(content.nodes ?? [])
        // @ts-ignore $TsFixMe
        .filter(and([isTextualNode, not(isEmptyParagraph)]))
        .get(),
      A.map(toNodeWordCount),
      limitToCount(count),
      fromEntries,
      applyToContent(content)
    );

const removeNodesById =
  (content: RichContent) =>
  (ids: Node['id'][]): RichContent =>
    modify(content).set(n => (ids.includes(n.id) ? [] : [n]));

const byNonTextNodesCount =
  (count: number) =>
  (content: RichContent): RichContent =>
    pipe(
      extract(content?.nodes ?? [])
        .filter(isNonTextualNode)
        .map(toId)
        .get()
        .slice(count),
      removeNodesById(content)
    );

const withFallback = (
  limits: ContentLimits
): { nodeCount: number; wordCount: number; maxPlugins: number } => ({
  ...{
    nodeCount: Infinity,
    wordCount: Infinity,
    maxPlugins: Infinity,
  },
  ...limits,
});

const removeEmptyContainers = (content: RichContent): RichContent =>
  modify(content)
    .filter(n =>
      [
        Node_Type.LIST_ITEM,
        Node_Type.ORDERED_LIST,
        Node_Type.BULLETED_LIST,
        Node_Type.BLOCKQUOTE,
        Node_Type.TABLE,
        Node_Type.COLLAPSIBLE_LIST,
      ].includes(n.type)
    )
    .set(n =>
      n.nodes?.length === 0
        ? []
        : n.type === Node_Type.LIST_ITEM && n.nodes.every(nChild => nChild.nodes.length === 0)
        ? []
        : [n]
    );

const removeEmptyCollapsibleListTitleAndBody = (content: RichContent): RichContent =>
  modify(content)
    .filter(n =>
      [Node_Type.COLLAPSIBLE_ITEM_TITLE, Node_Type.COLLAPSIBLE_ITEM_BODY].includes(n.type)
    )
    .set(n => (!n.nodes || n.nodes.length === 0 ? [] : [n]));

const removeEmptyCollapsibleListItems = (content: RichContent): RichContent =>
  modify(content)
    .filter(n => n.type === Node_Type.COLLAPSIBLE_ITEM)
    .set(n => {
      if (!n.nodes || n.nodes.length === 0) {
        return [];
      }

      const hasTitleAndBody = (node: Node): boolean =>
        Boolean(
          node.nodes &&
            node.nodes.some(child => child.type === Node_Type.COLLAPSIBLE_ITEM_TITLE) &&
            node.nodes.some(child => child.type === Node_Type.COLLAPSIBLE_ITEM_BODY)
        );

      return !hasTitleAndBody(n) ? [] : [n];
    });

const removeEmptyCollapsibleLists = (content: RichContent): RichContent =>
  modify(content)
    .filter(n => n.type === Node_Type.COLLAPSIBLE_LIST)
    .set(n => (!n.nodes || n.nodes?.length === 0 ? [] : [n]));

const removeInvalidCollapsibleLists = (content: RichContent): RichContent =>
  pipe(
    content,
    removeEmptyCollapsibleListTitleAndBody,
    removeEmptyCollapsibleListItems,
    removeEmptyCollapsibleLists
  );

const removeEmptyRows = (content: RichContent): RichContent =>
  modify(content)
    .filter(n => [Node_Type.TABLE_ROW, Node_Type.COLLAPSIBLE_ITEM].includes(n.type))
    .set(n => (n.nodes?.every(cell => cell.nodes?.length === 0) ? [] : [n]));

const mergeEmptyParagraphs = (content: RichContent): RichContent =>
  pipe(
    content,
    getFilteredNodes(
      (node, { previousSibling }) =>
        !!(isEmptyParagraph(node) && previousSibling && isEmptyParagraph(previousSibling))
    ),
    nodes => nodes.map(toId),
    removeNodesById(content)
  );

/**
 * Truncates RichContent by node count, word count, and non-text nodes count.
 */
export const truncateRichContent = (
  content: RichContent,
  limits: ContentLimits,
  options?: TruncateOptions
) => {
  const { nodeCount, wordCount, maxPlugins } = withFallback(limits);
  return pipe(
    content,
    byNonTextNodesCount(maxPlugins),
    byNodeCount(nodeCount, options?.selectCountableNodes || defaultSelectCountableNodes),
    byWordsCount(wordCount),
    removeEmptyRows,
    removeEmptyContainers,
    removeInvalidCollapsibleLists,
    options?.keepEmptyParagraphs ? identity : mergeEmptyParagraphs
  );
};
